import { IFile, IFoldersWithMaterial } from '@sdk/contracts';
import { Checkbox, Button, Popconfirm, Popover, Select } from 'antd';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ActionsWrapper, SelectWrapper, Wrapper } from './MaterialActions.styles';

import { Icon } from '@/components/Icon';
import { useCourseDetail, useMoveOrRemoveMaterial, useMutateCourses } from '@/store';

type Props = {
  folder: IFoldersWithMaterial;
  material: IFile[];
};

export const MaterialActions: FC<Props> = ({ folder, material }) => {
  const { t } = useTranslation(['CourseTranslations', 'CoursesTranslations']);
  const [moveFolder, setMoveFolder] = useState<string>();
  const { course } = useCourseDetail();
  const { isWorking, moveMaterialBetweenFolders, removeMaterialInFolder } = useMutateCourses();
  const { data, setData } = useMoveOrRemoveMaterial();

  const isChecked = useMemo(
    () => material.length > 0 && material.every(m => data?.includes(m)),
    [data, material]
  );
  const isDisabled = useMemo(() => !material.some(m => data?.includes(m)), [data, material]);
  const isMoveDisabled =
    isDisabled || !course?.foldersWithMaterial || course.foldersWithMaterial.length <= 1;

  const onRemove = () => {
    if (!course) return;
    const materialToRemove = material.filter(m => data?.includes(m));
    removeMaterialInFolder(course._id, folder, materialToRemove, () =>
      setData(data?.filter(d => !materialToRemove.includes(d)))
    );
  };

  const onMove = () => {
    if (!course || !moveFolder) return;
    const materialToMove = material.filter(m => data?.includes(m));
    moveMaterialBetweenFolders(
      {
        courseId: course._id,
        originFolder: folder._id,
        destinationFolder: moveFolder,
        materialIds: materialToMove
      },
      {
        onSuccess: () => {
          setData(data?.filter(d => !materialToMove.includes(d)));
          setMoveFolder(undefined);
        }
      }
    );
  };

  const handleCheckBox = () => {
    if (isChecked) {
      setData(undefined);
      return;
    }

    setData(material);
  };

  const getFolderOptions = () => {
    return course?.foldersWithMaterial
      .filter(f => f._id !== folder._id)
      .map(f => ({ value: f._id, label: f.name }));
  };

  const handleChange = (f: string) => setMoveFolder(f);

  if (material.length === 0) return null;

  return (
    <Wrapper>
      <Checkbox checked={isChecked} onClick={handleCheckBox}>
        {t('CoursesTranslations:SELECT_ALL')}
      </Checkbox>
      <ActionsWrapper>
        <Popover
          content={
            <SelectWrapper>
              <Select
                style={{ width: '100%' }}
                value={moveFolder}
                onChange={handleChange}
                options={getFolderOptions()}
              />
              <Button
                loading={isWorking}
                size="small"
                type="primary"
                onClick={onMove}
                disabled={!moveFolder || isMoveDisabled}>
                {t('CourseTranslations:MOVE')}
              </Button>
            </SelectWrapper>
          }
          title={t('CourseTranslations:MOVE_TO_OTHER_FOLDER')}>
          <Button
            loading={isWorking}
            size="small"
            type="link"
            icon={<Icon i="FolderMoveTo" />}
            disabled={isMoveDisabled}
          />
        </Popover>
        <Popconfirm
          onConfirm={onRemove}
          title={t('CourseTranslations:ARE_YOU_SURE_TO_REMOVE_SELECTED')}
          disabled={isDisabled}>
          <Button
            loading={isWorking}
            size="small"
            type="link"
            danger
            icon={<Icon i="TrashCan" />}
            disabled={isDisabled}
          />
        </Popconfirm>
      </ActionsWrapper>
    </Wrapper>
  );
};
